import { QUERY_PARAMS, ROUTES } from '../../routes/consts';
import { StepConfig } from '../../shared/MultiStepDialog';
import { prependHttpsIfNeeded } from '../GithubManifestInstallationDialog/utils';
import { STEP_IDS } from './consts';

export const getDefaultFurthestStepIndex = (
  pathname: string,
  search: string,
  connectReposRoute: string,
  steps: StepConfig[],
): number => {
  const isOAuthCallback = pathname === connectReposRoute;
  const params = new URLSearchParams(search);
  const hasOAuthParams =
    params.has(QUERY_PARAMS.STATUS) || params.has(QUERY_PARAMS.APP_CONFIG_ID);

  if (!isOAuthCallback || !hasOAuthParams) return 0;

  const connectReposIndex = steps.findIndex(
    (step) => step.id === STEP_IDS.CONNECT_REPOSITORIES,
  );

  return connectReposIndex === -1 ? 0 : connectReposIndex;
};

/**
 * Builds the navigation target for closing the wizard back to the integrations page,
 * preserving current query params (OAuth callback params are already stripped by ConnectRepositories).
 */
export const getIntegrationsCloseTarget = () => ({
  pathname: ROUTES.INTEGRATIONS,
  search: location.search,
});

export const getFirstSegmentOfUrl = (url: URL) =>
  url.pathname.replace(/^\//, '').split('/')[0];

/** Split a textarea value into trimmed, non-empty lines (one group URL per line). */
export const splitGitlabUrlLines = (value: string): string[] =>
  value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

/**
 * Normalizes a GitLab group URL to "<host><path>" with trailing slashes stripped
 * so prefix-based overlap detection works regardless of trailing-slash or case
 * differences in the host. Returns undefined when the URL cannot be parsed.
 */
export const normalizeGitlabGroupUrl = (line: string): string | undefined => {
  try {
    const url = new URL(prependHttpsIfNeeded(line));
    const path = url.pathname.replace(/\/+$/, '');

    return `${url.host.toLowerCase()}${path}`;
  } catch {
    return undefined;
  }
};

/**
 * Returns parent/child pairs from a list of GitLab group URLs. A URL is a
 * "parent" of another when its normalized form (host + path) is a strict prefix
 * of the other's, ending at a path-segment boundary — `acme` matches
 * `acme/team1` but not `acme-corp`. Lines that fail URL parsing are skipped.
 */
export const findGitlabGroupUrlOverlaps = (
  lines: string[],
): Array<{ parent: string; child: string }> => {
  const valid = lines
    .map((raw) => ({ raw, norm: normalizeGitlabGroupUrl(raw) }))
    .filter(
      (entry): entry is { raw: string; norm: string } =>
        entry.norm !== undefined,
    );

  const overlaps: Array<{ parent: string; child: string }> = [];

  for (let i = 0; i < valid.length; i++) {
    for (let j = 0; j < valid.length; j++) {
      if (i === j) continue;

      if (valid[j].norm.startsWith(`${valid[i].norm}/`)) {
        overlaps.push({ parent: valid[i].raw, child: valid[j].raw });
      }
    }
  }

  return overlaps;
};
